import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Skeleton } from '@/components/ui/skeleton'
import { SendDocumentDialog } from '@/components/billing/SendDocumentDialog'
import { RenewContractDialog } from '@/components/contracts/RenewContractDialog'
import { DeleteContractDialog } from '@/components/contracts/DeleteContractDialog'
import { TerminateContractDialog } from '@/components/contracts/TerminateContractDialog'
import { ContractDetailInfoSection } from '@/components/contracts/ContractDetailInfoSection'
import { LoadingOverlay } from '@/components/shared'
import { ContractDetailInformationCards } from '@/components/contracts/ContractDetailInformationCards'
import { ContractDetailHistoryCard } from '@/components/contracts/ContractDetailHistoryCard'
import { ContractDetailInterventionsCard } from '@/components/contracts/ContractDetailInterventionsCard'
import { JobDetailDrawer } from '@/components/jobs/JobDetailDrawer'
import {
  CONTRACT_DETAIL_DOCUMENTS_QUERY,
  CONTRACT_DETAIL_INTERVENTIONS_QUERY,
  CONTRACT_DETAIL_TAB_STALE_TIME,
  contractDocumentsQueryOptions,
  contractHistoryQueryOptions,
  contractInterventionsQueryOptions,
  useContract,
  useContractHistory,
  useContractInterventions,
  useContractDocuments,
} from '@/hooks/useContracts'
import { invalidatePortalMetricQueries } from '@/hooks/usePortalMetrics'
import { useListParams } from '@/hooks/useListParams'
import { useBreadcrumbLabel } from '@/contexts/BreadcrumbContext'
import { useTopBarActions } from '@/contexts/TopBarActionsContext'
import { contractsApi } from '@/services/contracts'
import { getApiErrorMessage } from '@/services/api'
import { toast } from '@/components/ui/toast'
import { primaryEmail } from '@/lib/clientContacts'

// Figma 3475:169698 — Tag Toggle Group (pill tabs on a raised white bar).
const tabBarShadow =
  'shadow-[0px_8px_16px_-4px_rgba(38,45,66,0.1),0px_1px_4px_-4px_rgba(38,45,66,0.05)]'
const tabTriggerClass =
  'shrink-0 rounded-lg border-0 px-3 py-1.5 text-sm font-medium leading-[1.4] text-[#3d434e] shadow-none transition-colors hover:text-[#171a20] data-[state=active]:bg-[#ff5a1f] data-[state=active]:text-white data-[state=active]:shadow-none'

// 2.8.5 View contract details - header summary + tabbed Information /
export default function ContractDetailPage() {
  const { t } = useTranslation(['contracts', 'clients', 'common'])
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const qc = useQueryClient()
  const { data, isLoading, isError } = useContract(id)
  // Top navi (Figma 3475:169624): Contracts / {number} + search. No CTA —
  // the list-page "New Contract" button is hidden on this instance.
  useBreadcrumbLabel(data?.data?.number ?? undefined)
  useTopBarActions(null)

  const [tab, setTab] = useState('information')
  const [sendOpen, setSendOpen] = useState(false)
  const [renewOpen, setRenewOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [terminateOpen, setTerminateOpen] = useState(false)
  const [exportingPdf, setExportingPdf] = useState(false)
  const selectedJobId = searchParams.get('job')
  const selectedJobTab = searchParams.get('job_tab') ?? undefined

  const documents = useContractDocuments(id, CONTRACT_DETAIL_DOCUMENTS_QUERY, {
    staleTime: CONTRACT_DETAIL_TAB_STALE_TIME,
  })
  const refreshDocuments = () =>
    qc.invalidateQueries({ queryKey: ['contracts', 'documents', id] })

  const hist = useListParams<Record<string, never>>({})
  const history = useContractHistory(id, hist.query, {
    enabled: tab === 'history',
    staleTime: CONTRACT_DETAIL_TAB_STALE_TIME,
  })
  const inter = useListParams({
    sort_column: 'scheduled_date',
    sort_order: 'desc' as const,
  })
  const interventions = useContractInterventions(id, inter.query, {
    staleTime: CONTRACT_DETAIL_TAB_STALE_TIME,
  })

  const preloadTab = (nextTab: string) => {
    if (!id || nextTab === tab) return

    if (nextTab === 'information') {
      void qc.prefetchQuery(
        contractDocumentsQueryOptions(
          id,
          CONTRACT_DETAIL_DOCUMENTS_QUERY,
          CONTRACT_DETAIL_TAB_STALE_TIME
        )
      )
      void qc.prefetchQuery(
        contractInterventionsQueryOptions(
          id,
          CONTRACT_DETAIL_INTERVENTIONS_QUERY,
          CONTRACT_DETAIL_TAB_STALE_TIME
        )
      )
    } else if (nextTab === 'history') {
      void qc.prefetchQuery(
        contractHistoryQueryOptions(
          id,
          hist.query,
          CONTRACT_DETAIL_TAB_STALE_TIME
        )
      )
    } else if (nextTab === 'interventions') {
      void qc.prefetchQuery(
        contractInterventionsQueryOptions(
          id,
          inter.query,
          CONTRACT_DETAIL_TAB_STALE_TIME
        )
      )
    }
  }

  useEffect(() => {
    if (searchParams.get('send') !== '1') return
    // One-shot: open the send dialog then drop ?send=1 so returning via back
    // navigation (e.g. after cancelling job planning) does not reopen it.
    /* eslint-disable-next-line react-hooks/set-state-in-effect */
    setSendOpen(true)
    const next = new URLSearchParams(searchParams)
    next.delete('send')
    const search = next.toString()
    navigate({ search: search ? `?${search}` : '' }, { replace: true })
  }, [searchParams, navigate])

  if (isLoading) {
    return (
      <div className='space-y-4'>
        <Skeleton className='h-8 w-64' />
        <Skeleton className='h-96 w-full' />
      </div>
    )
  }
  if (isError) {
    return (
      <div className='py-10 text-center'>
        <p className='text-destructive'>{t('detail.loadError')}</p>
        <Button
          variant='outline'
          className='mt-4'
          onClick={() => navigate('/contracts')}
        >
          {t('detail.backToContracts')}
        </Button>
      </div>
    )
  }
  if (!data?.data) {
    return (
      <div className='py-10 text-center'>
        <p className='text-muted-foreground'>{t('detail.notFound')}</p>
        <Button
          variant='outline'
          className='mt-4'
          onClick={() => navigate('/contracts')}
        >
          {t('detail.backToContracts')}
        </Button>
      </div>
    )
  }

  const c = data.data
  const canScheduleIntervention = c.status !== 'terminated'

  const scheduleForContract = () =>
    navigate(`/jobs/new?client_id=${c.client_id}&contract_id=${c.id}`)

  const openJobDrawer = (jobId: string, tab?: string) => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev)
        next.set('job', jobId)
        if (tab) next.set('job_tab', tab)
        else next.delete('job_tab')
        return next
      },
      { replace: false }
    )
  }

  const closeJobDrawer = () => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev)
        next.delete('job')
        next.delete('job_tab')
        return next
      },
      { replace: false }
    )
  }

  const exportPdf = async () => {
    setExportingPdf(true)
    try {
      const res = await contractsApi.pdf(c.id)
      if (!res.data?.url) throw new Error(t('shared:exportDialog.error'))
      window.open(res.data.url, '_blank', 'noopener')
      toast({ variant: 'success', title: t('shared:exportDialog.success') })
    } catch (e) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(e, t('detail.pdfError')),
      })
    } finally {
      setExportingPdf(false)
    }
  }

  return (
    <div className='flex flex-col gap-4 pb-6'>
      <ContractDetailInfoSection
        contract={c}
        jobs={interventions.data?.data ?? []}
        onSchedule={scheduleForContract}
        onExtend={() => setRenewOpen(true)}
        onExportPdf={exportPdf}
        onEdit={() => navigate(`/contracts/${c.id}/edit`)}
        onSend={() => setSendOpen(true)}
        onTerminate={() => setTerminateOpen(true)}
        onDelete={() => setDeleteOpen(true)}
      />

      <Tabs value={tab} onValueChange={setTab} className='flex flex-col gap-4'>
        <div
          className={`w-full overflow-x-auto rounded-xl border border-[#e7e9ef] bg-white p-2 ${tabBarShadow}`}
        >
          <TabsList className='h-auto w-max justify-start gap-1 overflow-x-visible border-0 bg-transparent p-0'>
            <TabsTrigger
              value='information'
              className={tabTriggerClass}
              onPointerEnter={() => preloadTab('information')}
              onFocus={() => preloadTab('information')}
            >
              {t('detail.tabs.information')}
            </TabsTrigger>
            <TabsTrigger
              value='history'
              className={tabTriggerClass}
              onPointerEnter={() => preloadTab('history')}
              onFocus={() => preloadTab('history')}
            >
              {t('detail.tabs.history')}
            </TabsTrigger>
            <TabsTrigger
              value='interventions'
              className={tabTriggerClass}
              onPointerEnter={() => preloadTab('interventions')}
              onFocus={() => preloadTab('interventions')}
            >
              {t('detail.tabs.interventions')}
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value='information' className='mt-0'>
          <ContractDetailInformationCards
            contract={c}
            documents={documents.data?.data ?? []}
            documentsLoading={documents.isLoading}
            onViewAll={() => setTab('interventions')}
            onDocumentsChanged={refreshDocuments}
            onOpenJob={openJobDrawer}
          />
        </TabsContent>

        <TabsContent value='history' className='mt-0'>
          <ContractDetailHistoryCard
            data={history.data}
            isLoading={history.isLoading}
            isError={history.isError}
            page={hist.page}
            pageSize={hist.limit}
            onPageChange={hist.setPage}
            onPageSizeChange={hist.setLimit}
          />
        </TabsContent>

        <TabsContent value='interventions' className='mt-0'>
          <ContractDetailInterventionsCard
            data={interventions.data}
            isLoading={interventions.isLoading}
            isError={interventions.isError}
            page={inter.page}
            pageSize={inter.limit}
            onPageChange={inter.setPage}
            onPageSizeChange={inter.setLimit}
            canSchedule={canScheduleIntervention}
            onSchedule={scheduleForContract}
            onOpenJob={openJobDrawer}
          />
        </TabsContent>
      </Tabs>

      <SendDocumentDialog
        open={sendOpen}
        onOpenChange={setSendOpen}
        title={t('detail.send.title')}
        defaultTo={primaryEmail(c.client)}
        defaultSubject={t('detail.send.subject', {
          number: c.number ?? '',
        }).trim()}
        defaultBody={t('detail.send.body')}
        showSignature
        signatureDefault={c.require_signature ?? false}
        successMessage={t('detail.send.success')}
        errorMessage={t('detail.send.error')}
        onSend={async body => {
          await contractsApi.sendSignature(c.id, {
            recipient_email: body.recipient_email ?? '',
            require_signature: body.request_signature,
            message: body.message,
          })
          invalidatePortalMetricQueries(qc)
        }}
      />

      <RenewContractDialog
        open={renewOpen}
        onOpenChange={setRenewOpen}
        contract={c}
      />

      <DeleteContractDialog
        contract={deleteOpen ? c : null}
        onOpenChange={setDeleteOpen}
        redirectToList
      />

      <TerminateContractDialog
        key={terminateOpen ? c.id : 'closed'}
        contract={terminateOpen ? c : null}
        onOpenChange={setTerminateOpen}
      />

      <JobDetailDrawer
        jobId={selectedJobId}
        initialTab={selectedJobTab}
        onClose={closeJobDrawer}
      />
      <LoadingOverlay open={exportingPdf} />
    </div>
  )
}
